import { TableViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/TableViewColumnDefinition'
import { searchData } from '@services/commonServices'
import cloneDeep from 'lodash/cloneDeep'
import differenceBy from 'lodash/differenceBy'
import eq from 'lodash/eq'
import filter from 'lodash/filter'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import forEach from 'lodash/forEach'
import includes from 'lodash/includes'
import intersectionBy from 'lodash/intersectionBy'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isPlainObject from 'lodash/isPlainObject'
import isString from 'lodash/isString'
import map from 'lodash/map'
import omit from 'lodash/omit'
import remove from 'lodash/remove'
import slice from 'lodash/slice'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import uniqWith from 'lodash/uniqWith'
import memoize from 'memoize-one'
import { action, computed, observable } from 'mobx'

import { LETTERS, RowTypes, SELECT_FILTER } from '../common/constants'
import { StripTableMap, TableRow } from '../common/modals'

export class StandardTableViewStore {
  keyForInitials: string
  rowHeight: number
  listRefs: any
  listOuterRefs: any
  headerRef: any
  tableClientHeight: number
  colDefList: Array<TableViewColumnDefinition> = []
  filterPopOveData: Array<any>
  maxSelectionLimit: number
  setSelectedData?: any

  @observable tableData: Array<any>
  @observable letterToHighlight: string = '#'
  @observable scrollLeft: number = 0
  @observable filteredColumns = {}
  @observable searchText = ''
  @observable columFilterData = {
    selectedColumnId: null,
    anchorEl: null,
    selectedColumnName: null,
    selectedLabelKey: null
  }
  @observable selectedRows: Array<any> = []
  // @observable selectedAll: boolean = false
  @observable isFilterPopOpen: boolean = false
  @observable filteredIds: Array<any> = []
  @observable disabledRows: any[] = []
  @observable allowDisabledRowsSelection = true

  @observable selectedFilterID = SELECT_FILTER[0]?.id // default selected id

  @observable isHeaderSelection = false

  @action
  setAllowDisabledRowsSelection = allowDisabledRowsSelection =>
    (this.allowDisabledRowsSelection = allowDisabledRowsSelection)

  @action
  setPreSelectedRows = data => {
    this.selectedRows = [...data]
  }

  @action
  setDisabledRows = data => {
    this.disabledRows = [...data]
  }

  @action
  setHeaderSelection = val => {
    this.isHeaderSelection = val
  }

  /**
   * To check single row checkbox
   */
  @action
  onCheckboxClick = row => e => {
    const selectedRows = cloneDeep(this.selectedRows)
    if (e.target.checked) {
      if (selectedRows.length < this.maxSelectionLimit) {
        selectedRows.push(row)
      }
    } else {
      const indexToRemoveId = findIndex(selectedRows, { id: row.id })
      if (indexToRemoveId > -1) {
        selectedRows.splice(indexToRemoveId, 1)
      }
    }
    this.selectedRows = selectedRows
  }

  /**
   * To clear checkbox selection
   */
  @action clearSelectedRows = () => {
    if (this.isHeaderSelection) return this.setHeaderSelection(false) // set selection to false
    this.selectedRows = []
    this.closeFilterPopOver()
    if (this.setSelectedData) {
      this.setSelectedData(this.selectedRows)
    }
  }

  @action
  onAllSelectCheckboxClick = e => {
    const filetedIds = this.getFilteredIds()
    const data = filetedIds.length
      ? filter(this.tableData, record => includes(filetedIds, record.id))
      : this.tableData
    const maxSelectCount = this.maxSelectionLimit
      ? this.maxSelectionLimit - this.selectedRows?.length
      : 0
    if (e.target.checked) {
      // Filtered already selected records
      const selectedIds = uniq(map(this.selectedRows, record => record.id))
      const filteredData = filter(
        differenceBy(data, this.disabledRows, 'id'),
        record => !includes(selectedIds, record.id)
      )
      this.selectedRows = [
        ...this.selectedRows,
        ...slice(filteredData, 0, maxSelectCount)
      ]
    } else {
      let selectedRows = cloneDeep(this.selectedRows)
      const disabledSelectedRows = map(
        intersectionBy(this.disabledRows, this.selectedRows, 'id'),
        'id'
      )
      forEach(data, record => {
        remove(
          selectedRows,
          row => row.id === record.id && !includes(disabledSelectedRows, record.id)
        )
      })
      this.selectedRows = selectedRows
    }
  }

  setListRef = ref => {
    this.listRefs = ref
  }

  setListOuterRef = ref => {
    this.listOuterRefs = ref
  }

  setTableHeaderRef = ref => {
    this.headerRef = ref
  }

  @action
  openFilterPopOver = (id, name, labelKey) => event => {
    this.columFilterData.anchorEl = event.currentTarget
    this.columFilterData.selectedColumnId = id
    this.columFilterData.selectedColumnName = name
    this.columFilterData.selectedLabelKey = labelKey
    this.isFilterPopOpen = true
  }

  getDataToShowInPopOverFilter = () => {
    const allFilterValues = find(
      this.filterPopOveData,
      item => item.id === this.columFilterData.selectedColumnId
    )

    let values: any[] = []
    if (this.columFilterData.selectedColumnId) {
      // Filtered columns without current selected column
      const filteredColumnsWithoutCurrentColumn = omit(
        this.filteredColumns,
        `${this.columFilterData.selectedColumnId}`
      )

      if (!isEmpty(filteredColumnsWithoutCurrentColumn)) {
        // Build filtered data for filters applied except for filter on current selected column
        const filteredDataWithoutSelectedColumn = this.filterTableDataForColumns(
          this.tableData || [],
          filteredColumnsWithoutCurrentColumn
        )

        // Prepare filter pop-over data for current column using filteredDataWithoutSelectedColumn
        values = this.prepareFilterDataForColumn(
          filteredDataWithoutSelectedColumn,
          this.columFilterData.selectedColumnId
        )
        return uniqWith(values, eq)
      }
    }

    return allFilterValues?.value ?? []
  }

  /**
   * Action for resetting all applied filters
   */
  @action resetFilters = () => {
    this.filteredColumns = {}
    this.closeFilterPopOver()
  }

  @action
  closeFilterPopOver = () => {
    this.columFilterData.anchorEl = null
    this.columFilterData.selectedColumnId = null
    this.columFilterData.selectedColumnName = null
    this.isFilterPopOpen = false
  }

  @action
  applyColumnFilter = (isSelectAllChecked, filterList) => {
    const selectColumn = this.columFilterData.selectedColumnId
    if (isSelectAllChecked) {
      delete this.filteredColumns[selectColumn!]
    } else (this.filteredColumns as any)[`${selectColumn}`] = filterList

    this.closeFilterPopOver()
  }
  /**
   * applyFilterSelection
   * @param event
   */
  @action
  applyFilterSelection = event => {
    this.selectedFilterID = event?.target?.value
  }

  prepareFilterDataForColumn = (data, columnKey) => {
    let values: any[] = []
    forEach(data, currentItem => {
      const currentValue = currentItem[columnKey]
      if (isString(currentValue)) {
        values.push(currentValue)
      } else if (isArray(currentValue)) {
        /**
         * NOTE: Filter will now work for array of strings and array of objects. In case of array of object,
         * filter will only be performed on "label" key if it exist and is of type string.
         */
        currentValue.forEach(element => {
          if (isString(element)) {
            values.push(element)
          } else if (isPlainObject(element) && element.label && isString(element.label)) {
            values.push(element.label)
          }
        })
      }
    })

    return values
  }

  prepareDataForFilterPopOver = memoize((data, colDefList) => {
    const columnList = filter(colDefList, colDef => colDef.showColumnFilter === true)
    let columnWiseData: any[] = []
    map(columnList, ({ node: { key } }) => {
      let values = this.prepareFilterDataForColumn(data, key)
      columnWiseData.push({
        id: key,
        value: uniqWith(values, eq)
      })
    })
    this.filterPopOveData = columnWiseData
  })

  /**
   * isMatchingInitialsAlphabet
   * Returns true if given letter is an initial for passed table record
   * @param letter Letter to test
   * @item Record to Test
   */
  isMatchingInitialsAlphabet = (letter: string, item: any) => {
    const valueToTest = item[this.keyForInitials]
    const firstLetter = valueToTest.charAt(0).toUpperCase()
    if ((letter === '#' && /^[^A-Z]$/.test(firstLetter)) || letter === firstLetter) {
      return true
    } else {
      return false
    }
  }

  /**
   * Returns true if given letter is an initial for passed table record
   * @param item  Record to Test
   * @returns alphabetic char
   */
  getInitialsAlphabet = (item: any) => {
    const firstLetter = item?.charAt(0).toUpperCase()
    if (/[a-zA-Z]/.test(firstLetter)) return firstLetter
    return '#'
  }

  /**
   * getSectionHeight
   * Returns height of the letter row and rows listed with same
   * letter
   * @param element
   */
  getSectionHeight = letter => {
    return (
      this.dataToVirtualize.filter(item => item.initial === letter).length *
      this.rowHeight
    )
  }

  /**
   * getSectionIndex
   * Returns index of letter row which matches passed letter
   * @param letter
   */
  getSectionIndex = letter => {
    return this.dataToVirtualize.findIndex(
      item => item.type === RowTypes.sectionHeader && item.initial === letter
    )
  }

  /**
   * onSearchTextChange
   * @param searchText
   */
  @action handleSearchTextChange = e => {
    this.searchText = e.target.value
  }

  /**
   * Clear search text
   */
  @action clearSearchText = () => {
    this.searchText = ''
  }

  filterTableDataForColumns = (data, filteredColumns) => {
    return data.filter(o =>
      Object.keys(filteredColumns).every(k =>
        filteredColumns[k].some(f => {
          if (isString(o[k])) {
            return o[k] === f
          } else if (isArray(o[k])) {
            // eslint-disable-next-line array-callback-return
            return o[k].some(item => {
              if (isString(item)) {
                return item === f
              } else if (isPlainObject(item)) {
                return isString(item?.label) ? item.label === f : false
              }
            })
          }
          return false
        })
      )
    )
  }

  /**
   * filterTableData
   * Filter data by applied column filters
   */
  getFilteredData = () => {
    if (!isEmpty(this.filteredColumns)) {
      const data = cloneDeep(this.tableData)
      const filterData = this.filterTableDataForColumns(data, this.filteredColumns)
      return filterData
    }
  }

  getFilteredIds = () => {
    const data = !isEmpty(this.filteredColumns) ? this.getFilteredData() : this.tableData
    let selectedIds: any[] = []
    forEach(data, record => {
      selectedIds.push(record.id)
    })
    return selectedIds
  }

  @computed get canCheckedSelectAll() {
    const data = !isEmpty(this.filteredColumns) ? this.getFilteredData() : this.tableData

    //Filter out already selected rows from data
    const selectedIds = uniq(map(this.selectedRows, record => record.id))
    const remainingRows = filter(data, record => !includes(selectedIds, record.id))
    const canSelectAll = this.maxSelectionLimit
      ? this.selectedRows?.length + remainingRows?.length > this.maxSelectionLimit
        ? false
        : true
      : false
    const disabledRowIds = uniq(map(this.disabledRows, record => record.id))
    return {
      canSelectAll: canSelectAll,
      showMaxLimitMessage: !canSelectAll && data?.length,
      disableSelectAll:
        data?.length > 0 &&
        data?.length === disabledRowIds?.length &&
        isEqual(sortBy(disabledRowIds), sortBy(selectedIds))
    }
  }

  @computed get dataForBulkUpdateComponent() {
    return {
      filteredIds: this.getFilteredIds(),
      selectedRows: this.selectedRows,
      filteredRows: this.getFilteredData(),
      clearFilters: this.resetFilters
    }
  }

  /**
   * filterTableData
   * Filter data by search key
   */
  @action getSearchedData = () => {
    /**
     * If search key is present filter clients
     */
    if (this.searchText) {
      const data = cloneDeep(this.tableData)
      return searchData(
        data,
        this.searchText,
        this.colDefList.map(col => col.node.key)
      )
    }
  }

  /**
   * filterTableData
   * Filter data by search key
   */
  @action getSelectedFilterData = data => {
    const selectedIds = map(this.selectedRows, 'id') || []
    // Show all
    if (this.selectedFilterID === SELECT_FILTER[0]?.id) return data

    // Show selected
    if (this.selectedFilterID === SELECT_FILTER[1]?.id)
      return filter(data, obj => includes(selectedIds, obj?.id))

    // Show unselected
    if (this.selectedFilterID === SELECT_FILTER[2]?.id)
      return filter(data, obj => !includes(selectedIds, obj?.id))

    return data
  }

  /**
   * setScrollLeft
   * Syncs horizontal scroll to header row
   * @param scrollLeft
   */
  @action setScrollLeft = scrollLeft => {
    if (this.headerRef) {
      this.headerRef.scrollLeft = scrollLeft
    }
  }

  /**
   * Set scroll left for table
   * @param {number} scrollLeft - Scroll left offset
   */
  @action setScrollLeftForOuterRef = (scrollLeft: number) => {
    if (this.listOuterRefs) {
      this.listOuterRefs.scrollLeft = scrollLeft
    }
  }

  /**
   * setLetterToHighlight
   * Syncs letter to be highlighted on strip
   * @param letter
   */
  @action setLetterToHighlight = letter => {
    this.letterToHighlight = letter
  }

  /**
   * onLetterSelect
   * Syncs selected letter from the strip
   * and scrolls within table accordingly
   * @letter
   */
  @action onLetterSelect = e => {
    if (e && e.target) {
      const letter = e.target.id
      if (letter && this.stripTableMap) {
        const selected = this.stripTableMap.find(item => letter === item.initial)
        const lastIndex = this.stripTableMap.length - 1
        const maxScrollPos =
          this.stripTableMap[lastIndex].sectionHeight - this.tableClientHeight

        /**
         * Observed that if we scrollToItem who's scroll top
         * is greater than max possible scroll, lib triggers
         * onScroll handler. To avoid this ensure that
         * at max we will equal to maxScrollPos
         */
        if (selected && this.listRefs) {
          this.setLetterToHighlight(letter)
          if (selected.scrollTop < maxScrollPos) {
            this.listRefs.scrollToItem(selected.startIndex, 'start')
          } else {
            const letterToHighlight = this.stripTableMap.find(
              item => maxScrollPos <= item.sectionHeight
            )
            if (letterToHighlight) {
              this.listRefs.scrollTo(maxScrollPos)
            }
          }
        }
      }
    }
  }

  /**
   * onVerticalTableScroll
   * To handle vertical scroll of
   * standard table
   */
  onVerticalTableScroll = ({
    scrollDirection,
    scrollOffset,
    scrollUpdateWasRequested
  }) => {
    if (
      this.keyForInitials &&
      !scrollUpdateWasRequested &&
      !isEmpty(this.stripTableMap)
    ) {
      const selected = this.stripTableMap.find(item => scrollOffset <= item.sectionHeight)
      if (selected) {
        this.setLetterToHighlight(selected.initial)
      }
    }
  }

  @computed get isSelectAllSet() {
    let data = !isEmpty(this.filteredColumns) ? this.getFilteredData() : this.tableData
    data = this.searchText ? this.getSearchedData() : data
    data = this.getSelectedFilterData(data)
    const selectedIds = uniq(map(this.selectedRows, record => record.id))
    const disabledRowIds = uniq(map(this.disabledRows, record => record.id))
    let isAllSelected = data?.length
    // if not in selected
    // and if disabled rows aren't shown as selected
    // and in disabled rows
    // break
    const considerForSelection = id =>
      this.allowDisabledRowsSelection
        ? !includes(selectedIds, id)
        : !includes(selectedIds, id) && !includes(disabledRowIds, id)
    forEach(data, record => {
      if (considerForSelection(record.id)) {
        isAllSelected = false
        return
      }
    })
    return isAllSelected
  }

  /**
   * setTableData
   * Initialize data required to load the table
   * @pram data -  Raw table data
   * @param keyForInitials - Key to use while JumpTo initial functionality
   * @param rowHeight - height of table row
   */
  @action setTableData = (
    data: any,
    keyForInitials: string,
    rowHeight: number,
    tableClientHeight: number,
    colDefList: Array<TableViewColumnDefinition>,
    maxSelectionLimit: number,
    setSelectedData?: any
  ) => {
    this.setSelectedData = setSelectedData
    this.prepareDataForFilterPopOver(data, colDefList)
    this.tableData = cloneDeep(data)
    this.keyForInitials = keyForInitials
    this.rowHeight = rowHeight
    this.tableClientHeight = tableClientHeight
    this.colDefList = colDefList
    this.maxSelectionLimit = maxSelectionLimit
  }

  /**
   * dataToVirtualize
   * Computes data to load on table
   */
  @computed get dataToVirtualize(): Array<TableRow> {
    let data = !isEmpty(this.filteredColumns) ? this.getFilteredData() : this.tableData
    data = this.searchText ? this.getSearchedData() : data
    data = this.getSelectedFilterData(data)

    if (this.setSelectedData) {
      this.setSelectedData(this.selectedRows)
    }
    if (isEmpty(data)) return []
    let rows: any[]

    if (!isEmpty(this.keyForInitials)) {
      rows = LETTERS.reduce((acc, letter) => {
        acc.push({
          id: letter,
          initial: letter,
          type: RowTypes.sectionHeader
        })
        const recordsWithLetter = data
          .filter(record => this.isMatchingInitialsAlphabet(letter, record))
          .map(item => ({
            ...item,
            type: RowTypes.record,
            initial: letter
          }))

        if (recordsWithLetter && recordsWithLetter.length) {
          acc = acc.concat(recordsWithLetter)
        }
        return acc
      }, [])
    } else {
      rows = map(data, item => ({
        ...item,
        type: RowTypes.record
      }))
    }

    return rows
  }

  @computed get stripTableMap(): Array<StripTableMap> {
    if (isEmpty(this.dataToVirtualize) || isEmpty(this.keyForInitials)) return []
    return LETTERS.reduce(
      (acc, letter) => {
        const currentSectionHeight = this.getSectionHeight(letter)
        acc.height =
          acc.height + (currentSectionHeight ? currentSectionHeight : this.rowHeight)
        acc.result.push({
          initial: letter,
          startIndex: this.getSectionIndex(letter),
          scrollTop: acc.scrollTop,
          sectionHeight: acc.height
        })

        acc.scrollTop = acc.scrollTop + currentSectionHeight
        return acc
      },
      { result: [], height: 0, scrollTop: 0 }
    ).result
  }
}
